// Glass: surfaces that float over a body scrolling under them (the `.glass`
// rules and their reasons are in styles/index.css), and the scrolling body that
// goes under them. Made for a chat's post box; any conversation can use it.
//
// Promise:
// - The body scrolls under the dock and shows through it; at the end of the
//   scroll the last line rests fully above the dock. Running top down, the
//   dock is the last thing in the scroll and sticks to its bottom, so the
//   browser keeps its room (late fonts or a growing post box need nothing).
//   Running bottom up (a chat), the dock lies over the scroll instead, and the
//   scroll ends with a room of the dock's height: WebKit does not stick a
//   `bottom: 0` box in a `column-reverse` scroll, and on an iPhone the post
//   box of a chat opened over another (a child chat) went up with the rows
//   (2026-10-18).
// - The scrollbar stops above the dock.
// - Things that come and go on the glass (the "to the end" button) stay mounted
//   and turn transparent and inert: on iOS, putting a fixed glass surface in
//   while a finger scrolls made the scroll jump.
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
} from "react";

/** The class of a glass surface (`data-glass="read"` for one read on). */
export const GLASS = "glass";

/** A scroll this soon after the person's input is theirs. */
const USER_SCROLL_WINDOW_MS = 800;
/** Keys that scroll the focused body. */
const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

/**
 * A scrolling body with a glass dock at its bottom. `reverse` runs the body
 * bottom up (`column-reverse`: it opens at the end and stays there while it
 * grows). The dock's own children take the pointer; the strip around them
 * lets it through to the body.
 */
export function ScrollUnderGlass({
  dock,
  children,
  reverse = false,
  paneRef,
  paneProps,
  onUserScroll,
}: {
  dock: ReactNode;
  children: ReactNode;
  reverse?: boolean;
  paneRef?: Ref<HTMLDivElement>;
  /** More for the scrolling element (data attributes, onScroll). */
  paneProps?: HTMLAttributes<HTMLDivElement> & Record<`data-${string}`, string>;
  /** The person scrolled the body (wheel, touch, scrollbar, keys); not when on the dock. */
  onUserScroll?: () => void;
}) {
  const pane = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  // frontend-development: not a fetch; writes the dock's height for the scrollbar's track and, bottom up, the room at the scroll's end.
  useLayoutEffect(() => {
    const el = dockRef.current;
    const root = pane.current;
    if (!el || !root) return;
    const write = () => {
      root.style.setProperty("--glass-dock-height", `${el.offsetHeight}px`);
      // Bottom up, the dock lies over the scroll: it ends where the rows
      // end, left of the scrollbar, as it did inside the scroll.
      if (reverse) el.style.right = `${root.offsetWidth - root.clientWidth}px`;
    };
    write();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(write);
    ro.observe(root);
    // Its padding too: on a phone the room under the post box grows back
    // when writing ends (phone.css), its content unchanged; the room at the
    // scroll's end stayed short by that much (2026-10-26).
    ro.observe(el, { box: "border-box" });
    return () => ro.disconnect();
  }, [reverse]);
  const inputAt = useRef(Number.NEGATIVE_INFINITY);
  const lastTop = useRef(0);
  const markInput = () => {
    inputAt.current = performance.now();
  };
  const fromBody = (target: EventTarget | null) =>
    !(target instanceof Node && dockRef.current?.contains(target));
  // The dock keeps a strip of the ground under it: the body ends above that
  // strip (it does not run out below the glass), and the glass's shadow falls
  // on it instead of being cut by the scroll's edge.
  const dockEl = (
    <div
      ref={dockRef}
      data-glass-dock=""
      className={`pointer-events-none z-10 flex-none pb-4 *:pointer-events-auto ${reverse ? "absolute inset-x-0 bottom-0" : "sticky bottom-0 mt-auto"}`}
    >
      <div
        aria-hidden="true"
        data-glass-ground=""
        className="pointer-events-none! absolute inset-x-0 bottom-0 -z-10 h-4 bg-surface"
      />
      {dock}
    </div>
  );
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: only watches the person's scrolling input; it acts on nothing */}
      <div
        {...paneProps}
        ref={(el) => {
          pane.current = el;
          if (typeof paneRef === "function") paneRef(el);
          else if (paneRef) paneRef.current = el;
        }}
        // The person's own scroll is a scroll that moves the body right after
        // their wheel, finger, scrollbar thumb or scroll key. Input alone is
        // not enough: a wheel over a short body (nothing to scroll) or a
        // pinch zoom (ctrl + wheel) moves nothing. Growth and the app's own
        // writes come without input.
        onWheel={(e) => {
          if (!e.ctrlKey && fromBody(e.target)) markInput();
        }}
        onTouchMove={(e) => {
          if (fromBody(e.target)) markInput();
        }}
        onPointerDown={(e) => {
          // On the scroll itself, not a row: its scrollbar.
          if (e.target === e.currentTarget) markInput();
        }}
        onKeyDown={(e) => {
          if (SCROLL_KEYS.has(e.key) && fromBody(e.target)) markInput();
        }}
        onScroll={(e) => {
          paneProps?.onScroll?.(e);
          const el = e.currentTarget;
          if (
            el.scrollTop !== lastTop.current &&
            performance.now() - inputAt.current < USER_SCROLL_WINDOW_MS
          )
            onUserScroll?.();
          lastTop.current = el.scrollTop;
        }}
        className={`under-glass flex min-h-0 flex-1 overflow-y-auto ${
          reverse ? "flex-col-reverse" : "flex-col"
        }`}
      >
        {reverse ? (
          <>
            <div
              aria-hidden="true"
              className="flex-none"
              style={{ height: "var(--glass-dock-height, 0px)" }}
            />
            {children}
          </>
        ) : (
          <>
            {children}
            {dockEl}
          </>
        )}
      </div>
      {reverse && dockEl}
    </div>
  );
}

/** A round glass button with one icon (the darkest text color, cut out from what shows through). */
export function GlassIconButton({
  label,
  width = 46,
  height = 44,
  className = "",
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
  label: string;
  width?: number;
  height?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      // Pressing it keeps the focus where it was (the post box).
      onMouseDown={(e) => e.preventDefault()}
      {...rest}
      style={{ width, height, borderRadius: height / 2 }}
      className={`${GLASS} glass-icon inline-flex flex-none cursor-pointer items-center justify-center text-fg ${className}`}
    >
      {children}
    </button>
  );
}
